/**
 * WaComposerAiTools — botón de herramientas IA dentro del composer.
 *
 * Actions: reformular (4 tonos), corregir ortografía, traducir (4 idiomas).
 * Cada acción consume 1 crédito IA (deduct_ai_credit). Llama al edge function
 * unificado `wa-copilot` con `text` = borrador actual; al terminar reemplaza
 * el borrador mediante `onApply`.
 */

import { useState } from 'react';
import { Icon, Popover, ActionList, Spinner } from '@/lib/polaris';
import { MagicIcon } from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { useAuth } from '@smarty/shared';

interface Props {
  /** Borrador actual. Si está vacío, el botón se deshabilita. */
  text: string;
  /** Callback para reemplazar el borrador con el texto transformado. */
  onApply: (newText: string) => void;
}

type AiAction =
  | { kind: 'rephrase'; tone: 'formal' | 'casual' | 'empathic' | 'concise' }
  | { kind: 'fix_spelling' }
  | { kind: 'translate'; language: 'es' | 'en' | 'pt' | 'fr' };

interface CopilotResponse {
  output?: string;
  error?: string;
  detail?: string;
}

const TONE_LABELS: Record<'formal' | 'casual' | 'empathic' | 'concise', string> = {
  formal: 'Formal',
  casual: 'Cercano',
  empathic: 'Empático',
  concise: 'Conciso',
};

const LANG_LABELS: Record<'es' | 'en' | 'pt' | 'fr', string> = {
  es: 'Español',
  en: 'Inglés',
  pt: 'Portugués',
  fr: 'Francés',
};

export function WaComposerAiTools({ text, onApply }: Props) {
  const { tenantId } = useAuth();
  const toast = useAdminToastOptional();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const disabled = !text.trim() || loading;

  const run = async (action: AiAction) => {
    setActive(false);
    setLoading(true);
    try {
      const baseBody =
        action.kind === 'rephrase'
          ? { action: 'rephrase', text, tone: action.tone }
          : action.kind === 'translate'
          ? { action: 'translate', text, language: action.language }
          : { action: 'fix_spelling', text };
      const body = { ...baseBody, tenant_id: tenantId };

      const { data, error } = await supabase.functions.invoke<CopilotResponse>('wa-copilot', {
        body,
      });

      if (error) {
        devLog.warn('AI tools invoke error:', error);
        toast?.error('IA', 'No se pudo procesar el texto.');
        return;
      }
      if (data?.error) {
        if (data.error === 'no_credits') {
          toast?.error('IA', 'Sin créditos de IA disponibles.');
        } else if (data.error === 'no_ai_config') {
          toast?.error('IA', 'Configura el proveedor de IA en Ajustes.');
        } else {
          toast?.error('IA', 'La IA no pudo responder.');
        }
        return;
      }
      if (data?.output) {
        onApply(data.output.trim());
      }
    } catch (e) {
      devLog.warn('AI tools error:', e);
      toast?.error('IA', 'Error inesperado.');
    } finally {
      setLoading(false);
    }
  };

  const activator = (
    <button
      type="button"
      data-wa-trigger
      onClick={() => !disabled && setActive((v) => !v)}
      disabled={disabled}
      className="wa-icon-btn"
      title={disabled ? 'Escribe texto para usar IA' : 'Herramientas IA'}
      aria-label="Herramientas IA"
      style={{ opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {loading ? (
        <Spinner size="small" />
      ) : (
        <span style={{ color: active ? '#7b1fa2' : '#3b4a54' }}>
          <Icon source={MagicIcon} />
        </span>
      )}
    </button>
  );

  return (
    <Popover
      active={active}
      activator={activator}
      onClose={() => setActive(false)}
      preferredPosition="above"
      preferredAlignment="right"
    >
      <ActionList
        sections={[
          {
            title: 'Reformular',
            items: (Object.keys(TONE_LABELS) as Array<keyof typeof TONE_LABELS>).map((tone) => ({
              content: TONE_LABELS[tone],
              onAction: () => void run({ kind: 'rephrase', tone }),
            })),
          },
          {
            title: 'Corregir',
            items: [
              {
                content: 'Ortografía y gramática',
                onAction: () => void run({ kind: 'fix_spelling' }),
              },
            ],
          },
          {
            title: 'Traducir',
            items: (Object.keys(LANG_LABELS) as Array<keyof typeof LANG_LABELS>).map((lang) => ({
              content: LANG_LABELS[lang],
              onAction: () => void run({ kind: 'translate', language: lang }),
            })),
          },
        ]}
      />
    </Popover>
  );
}
